import SliderCaptcha, { ActionType, VerifyParam } from 'rc-slider-captcha';
import { ChangeEvent, MutableRefObject, memo } from 'react';
import { useTranslation } from 'react-i18next';

import { getWEAProgramImage } from '../../../../constants/images';
import { SVGIconType } from '../../../../constants/svgIcon';
import {
  LoadingSpinner,
  SvgIcon,
  TextButton,
  TextButtonSize,
  TextButtonVariant,
  TextInput,
} from '../../../Molecules';

import './WEALoginTemplate.css';

type TextInputProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type WEALoginTemplateProps = {
  email: TextInputProps;
  password: TextInputProps;
  isClicked: boolean;
  onClick: () => void;
  errorMessage: string;
  isLoading: boolean;
  onClickForget: () => void;
  showCaptcha: boolean;
  verified: boolean;
  onRequest: () => Promise<{
    bgUrl: string;
    puzzleUrl: string;
  }>;
  onVerify: (data: VerifyParam) => Promise<void>;
  actionRef: MutableRefObject<ActionType | undefined>;
  onClickCaptcha: () => void;
  onCloseCaptcha: () => void;
  onClickTerms: () => void;
  onClickPrivacy: () => void;
  onClickNavigateRegister: () => void;
};

export const WEALoginTemplate = memo(
  ({
    email,
    password,
    isClicked,
    onClick,
    errorMessage,
    isLoading,
    onClickForget,
    showCaptcha,
    verified,
    onRequest,
    onVerify,
    actionRef,
    onClickCaptcha,
    onCloseCaptcha,
    onClickTerms,
    onClickPrivacy,
    onClickNavigateRegister,
  }: WEALoginTemplateProps) => {
    const { t } = useTranslation();
    return (
      <div className="WEAlogin">
        <div className="centered-container" onClick={onCloseCaptcha}>
          <img
            className="icon"
            src={getWEAProgramImage('img2')}
            alt="Joyajoy Icon"
          />
          <p className="text-lg">{t('Login')}</p>
          <div className="login-bar">
            <TextInput
              value={email.value}
              onChange={email.onChange}
              placeholder={t('Email / Username')}
              isClicked={isClicked}
              style={{ backgroundColor: '#f0f0f0', border: '0' }}
            />
            <TextInput
              value={password.value}
              onChange={password.onChange}
              placeholder={t('Password')}
              isClicked={isClicked}
              type="password"
              style={{ backgroundColor: '#f0f0f0', border: '0' }}
            />
          </div>
          <div className="forgot-password-container">
            <span className="forgot-password" onClick={onClickForget}>
              {t('Forgot Password')}
            </span>
          </div>
          <div
            className="captcha-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="captcha-content">
              <SvgIcon
                className={`captcha-box ${verified ? 'visible' : ''}`}
                name={
                  verified
                    ? SVGIconType.ConfirmTick
                    : SVGIconType.SquareCheckboxEmpty
                }
                width={24}
                height={24}
                stroke="#333333"
                onClick={verified ? undefined : onClickCaptcha}
              />
              <p className="captcha-text text-sm">{t("I'm not a robot")}</p>
            </div>
            {showCaptcha && (
              <div className="captcha-slider">
                <SliderCaptcha
                  request={onRequest}
                  onVerify={onVerify}
                  bgSize={{ width: 250, height: 150 }}
                  tipText={{
                    default: t('Slide to fill in puzzle'),
                    loading: t('Loading...'),
                  }}
                  autoRequest={false}
                  actionRef={actionRef}
                  loadingBoxProps={{ text: t('Loading') }}
                  style={{
                    backgroundColor: '#F7F9FA',
                  }}
                />
              </div>
            )}
          </div>
          <div className="warning">{t(errorMessage)}</div>
          <TextButton
            text={t('Login')}
            size={TextButtonSize.Medium}
            variant={TextButtonVariant.WEABlue}
            onClick={onClick}
            disabled={
              !verified ||
              email.value.trim().length < 1 ||
              password.value.trim().length < 1
            }
          />
          <div className="footer">
            <p className="text-footer-black">
              {t(
                'By creating or logging into an account you are agreeing to our ',
              )}
            </p>
            <p className="text-footer-green" onClick={onClickTerms}>
              {t('Terms and Conditions')}
            </p>
            <p className="text-footer-black">{t(' and ')}</p>
            <p className="text-footer-green" onClick={onClickPrivacy}>
              {t('Privacy Statement')}
            </p>
          </div>
          <hr />
          <p className="text-footer-black">{t("Don't have an account?")}</p>
          <TextButton
            text={t('Sign Up')}
            size={TextButtonSize.Medium}
            variant={TextButtonVariant.WEABlue}
            onClick={onClickNavigateRegister}
          />
        </div>
        {isLoading && <LoadingSpinner loading={isLoading} />}
      </div>
    );
  },
);
